import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import {
  type ChartData,
  type ChartDataset,
  type ScatterDataPoint,
  type TooltipItem,
} from "chart.js";
import clsx from "clsx";
import { isEmpty, keyBy, omit } from "lodash";
import { Bar, Line } from "react-chartjs-2";
import { renderToString } from "react-dom/server";
import {
  Events,
  Link,
  Element as ScrollElement,
  scrollSpy,
} from "react-scroll";

import { Loading } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { LightSwitch } from "@components/utilities";
import { FETCH_CREATIVES } from "@gql/creative-analytics";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  useDesignElementManager,
  type DesignElementManagerType,
} from "@hooks/use-design-element-manager";
import {
  MAX_NUMBER,
  type FetchCreatives,
  type FetchCreativesVariables,
  type FetchCreatives_me_creatives_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { type MetricPoint } from "@north-beam/nb-common/metrics";

import { useAvailableMetricsByIdAtom, useGraphTypeAtom } from "../atoms";
import { useReportBodyState } from "../hooks/ca-control-state";
import { useDateRangeAsDates } from "../hooks/use-date-range";
import {
  getPlatformMetricFromAd,
  getPlatformName,
} from "../utils/platform-metrics";

import { CreativeAnalyticsLink } from "./ca-creative-type-bar";
import { ResizableComponent } from "./ca-resizeable-container";

export function CreativeAnalyticsCharts() {
  const { metrics: selectedMetrics, heroMetricId } = useReportBodyState();
  const metrics = useMemo(
    () => [heroMetricId, ...selectedMetrics],
    [heroMetricId, selectedMetrics],
  );
  const {
    attributionModel,
    attributionWindow,
    dateRange,
    selectedPlatforms,
    groupAdsByName,
    sorting,
    sortingOrder,
    searchText,
    activeStatus,
    creativeType,
    selectedAdsByAdKey,
    metricFilters,
  } = useReportBodyState();

  const {
    loadData,
    data: { loading, data },
  } = useNorthbeamLazyQuery<FetchCreatives, FetchCreativesVariables>(
    FETCH_CREATIVES,
  );

  const adsWithFullDailyMetricsByAdKey = useMemo(() => {
    return keyBy(data?.me.creatives.rows, "adKey");
  }, [data]);

  useEffect(() => {
    const adKeys = Object.keys(selectedAdsByAdKey);

    if (!adKeys.length) {
      loadData({
        variables: {
          dateRange,
          attributionMethod: attributionModel,
          attributionWindow,
          granularity: "ad",
          nbAccountingMode: "accrual",
          timeGranularity: "daily",
          offset: 0,
          limit: 0,
          dateCompression: false,
          platforms: [],
          sorting: {
            customComposed: sorting.customComposed,
            type: sorting.type,
            metric: {
              details: {
                accountingMode: "accrual",
                am: attributionModel,
                aw: attributionWindow,
                type: "",
                ...sorting.metric?.details,
              },
              ...sorting.metric,
            },
            order: sortingOrder,
          },
          status: activeStatus,
          searchText: searchText?.trim() ? searchText?.trim() : null,
          groupByName: groupAdsByName,
          creativeType,
          metricFilters: isEmpty(metricFilters) ? undefined : metricFilters,
        },
      });
      return;
    }

    loadData({
      variables: {
        dateRange,
        adKeys,
        attributionMethod: attributionModel,
        attributionWindow,
        granularity: "ad",
        nbAccountingMode: "accrual",
        timeGranularity: "daily",
        offset: 0,
        limit: 10_000,
        dateCompression: false,
        platforms: selectedPlatforms,
        groupByName: groupAdsByName,
        creativeType,
        metricFilters: isEmpty(metricFilters) ? undefined : metricFilters,
        sorting: {
          customComposed: sorting.customComposed,
          type: sorting.type,
          metric: {
            details: {
              accountingMode: "accrual",
              am: attributionModel,
              aw: attributionWindow,
              type: "",
              ...sorting.metric?.details,
            },
            ...sorting.metric,
          },
          order: sortingOrder,
        },
      },
    });
  }, [
    attributionModel,
    attributionWindow,
    dateRange,
    loadData,
    selectedAdsByAdKey,
    selectedPlatforms,
    sorting,
    sortingOrder,
    groupAdsByName,
    activeStatus,
    searchText,
    creativeType,
    metricFilters,
  ]);

  useEffect(() => {
    scrollSpy.update();
    return () => {
      Events.scrollEvent.remove("begin");
      Events.scrollEvent.remove("end");
    };
  }, []);

  return (
    <ResizableComponent height={55}>
      {({ currentHeight }) => {
        const remainderHeight = currentHeight - 500;

        return (
          <>
            {currentHeight <= 90 && (
              <div
                className={clsx(
                  "absolute top-5 right-1/2 translate-x-1/2",
                  "transform font-semibold z-[1]",
                )}
              >
                Charts
              </div>
            )}
            <div
              className="h-full flex flex-col bg-v2-primary items-center relative overflow-y-auto"
              style={{
                boxShadow: "0px -3px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div className="mt-[0.5rem]" />
              {currentHeight > 200 && loading && <Loading />}
              {currentHeight > 90 && !loading && (
                <>
                  <GraphControl
                    metrics={metrics}
                    adsWithFullDailyMetricsByAdKey={
                      adsWithFullDailyMetricsByAdKey
                    }
                  />
                  <div className="border-t-[1.5px] w-full mt-[1rem] mb-5" />
                  <div
                    className="w-full overflow-y-auto items-center flex flex-col"
                    id="graphsContainer"
                  >
                    {metrics.map((metric, indx) => {
                      return (
                        <CreativeAnalyticsGraph
                          key={metric}
                          metric={metric}
                          adsWithFullDailyMetricsByAdKey={
                            adsWithFullDailyMetricsByAdKey
                          }
                          className={clsx(indx > 0 && "mt-5")}
                        />
                      );
                    })}
                    <div
                      style={{
                        minHeight: remainderHeight,
                        maxHeight: remainderHeight,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        );
      }}
    </ResizableComponent>
  );
}

const MAX_GRAPH_NAV_ITEMS = 5;

function GraphControl({
  metrics,
  adsWithFullDailyMetricsByAdKey,
}: {
  metrics: string[];
  adsWithFullDailyMetricsByAdKey: Record<
    string,
    FetchCreatives_me_creatives_rows
  >;
}) {
  const [graphType, setGraphType] = useGraphTypeAtom();
  return (
    <div className="flex flex-row self-start w-full items-center">
      <div className="h-[3rem] flex ml-4 mb-2 flex-row ">
        {[...metrics].slice(0, MAX_GRAPH_NAV_ITEMS).map((metric) => {
          return <MetricControl key={metric} metric={metric} />;
        })}
        {metrics.length > MAX_GRAPH_NAV_ITEMS && (
          <Menu as="div" className="ml-2 relative">
            <MenuButton>
              <i
                className={clsx(
                  "font-semibold fa-regular fa-sharp cursor-pointer text-[#717680] text-lg",
                  "absolute top-2",
                  {
                    "fa-chevron-down": true,
                  },
                )}
              />
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform ease-out duration-100"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                className={clsx(
                  "absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y",
                  "divide-gray-100 rounded-md bg-v2-primary shadow-sm ring-1",
                  "ring-black ring-opacity-5 focus:outline-none",
                )}
              >
                <div className="py-1">
                  {[...metrics].slice(MAX_GRAPH_NAV_ITEMS).map((metric) => {
                    return (
                      <MenuItem key={metric}>
                        <DropdownMetricControl key={metric} metric={metric} />
                      </MenuItem>
                    );
                  })}
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        )}
      </div>
      <div className="ml-auto mr-10 flex flex-row items-center">
        <div className="flex flex-row items-center">
          Chart display:
          <div
            className={clsx(
              "cursor-pointer border  w-[2rem] h-[2.1rem] flex items-center justify-center",
              "rounded-lg ml-3",
              {
                "border-cosmic": graphType === "line",
                "border-gray+2": graphType !== "line",
              },
            )}
            onClick={() => setGraphType("line")}
          >
            <i className="fa-regular fa-sharp fa-sharp fa-chart-line" />
          </div>
          <div
            className={clsx(
              "cursor-pointer border w-[2rem] h-[2.1rem] flex items-center justify-center",
              "rounded-lg ml-3",
              {
                "border-cosmic": graphType === "bar",
                "border-gray+2": graphType !== "bar",
              },
            )}
            onClick={() => setGraphType("bar")}
          >
            <i className="fa-regular fa-sharp fa-chart-simple" />
          </div>
        </div>
        <div className="h-[2rem] border-l-[1.5px] border-gray mx-4" />
        <Ads adsWithFullDailyMetricsByAdKey={adsWithFullDailyMetricsByAdKey} />
      </div>
    </div>
  );
}

function Ads({
  adsWithFullDailyMetricsByAdKey,
}: {
  adsWithFullDailyMetricsByAdKey: Record<
    string,
    FetchCreatives_me_creatives_rows
  >;
}) {
  const { selectedAdsByAdKey, setSelectedAdsByAdKey } = useReportBodyState();
  return (
    <div className="flex flex-row items-center">
      {isEmpty(selectedAdsByAdKey) && <div>No ads selected</div>}
      {Object.values(selectedAdsByAdKey).map((ad, index) => {
        const fullAd = adsWithFullDailyMetricsByAdKey[ad.adKey];
        if (!fullAd) {
          return null;
        }
        return (
          <Ad
            name={ad.adVisualId}
            color={ad.adColouring.pointBackgroundColor}
            key={index}
            tooltip={fullAd.name ?? "N/A"}
            onClick={() => {
              setSelectedAdsByAdKey(omit(selectedAdsByAdKey, ad.adKey));
            }}
          />
        );
      })}
    </div>
  );
}
function Ad({
  name,
  color,
  tooltip,
  onClick,
}: {
  name: string;
  color: string;
  tooltip: string;
  onClick: () => void;
}) {
  return (
    <GenericTooltip content={tooltip} noInfoCircle>
      <div
        className="flex flex-row items-center cursor-pointer"
        onClick={onClick}
      >
        <div
          className="h-[1rem] w-[1rem] rounded-sm mr-1 ml-3"
          style={{ background: color }}
        />
        {name}
      </div>
    </GenericTooltip>
  );
}

function DropdownMetricControl({ metric }: { metric: string }) {
  const [isActive, setActive] = useState(false);
  const [availableMetricsById] = useAvailableMetricsByIdAtom();

  return (
    <Link
      key={metric}
      to={metric}
      spy={true}
      smooth={true}
      containerId="graphsContainer"
      className={clsx({
        "no-underline": true,
      })}
      offset={-10}
      onSetActive={() => setActive(true)}
      onSetInactive={() => setActive(false)}
      duration={200}
    >
      <div
        className={clsx("cursor-pointer my-4 mx-5", {
          "text-primary": isActive,
          "text-subtitle": !isActive,
        })}
      >
        {availableMetricsById[metric].name}
      </div>
    </Link>
  );
}

function MetricControl({ metric }: { metric: string }) {
  const [isActive, setActive] = useState(false);
  const [availableMetricsById] = useAvailableMetricsByIdAtom();

  return (
    <Link
      key={metric}
      to={metric}
      spy={true}
      smooth={true}
      containerId="graphsContainer"
      className={clsx({
        "no-underline": true,
      })}
      offset={-10}
      onSetActive={() => setActive(true)}
      onSetInactive={() => setActive(false)}
      duration={200}
    >
      <CreativeAnalyticsLink
        isActive={isActive}
        key={metric}
        text={availableMetricsById[metric].name}
      />
    </Link>
  );
}

const calculateValue = (
  calculate: (v: MetricPoint) => number,
  metricPoint: MetricPoint,
) => {
  const calculated = calculate(metricPoint);

  // Fixes edge case dividing by 0
  if (calculated === Infinity || Number.isNaN(calculated)) {
    return NaN;
  }

  return calculated < MAX_NUMBER ? calculated : MAX_NUMBER;
};

function CreativeAnalyticsGraph({
  metric,
  className,
  adsWithFullDailyMetricsByAdKey,
}: {
  metric: string;
  className?: string;
  adsWithFullDailyMetricsByAdKey: Record<
    string,
    FetchCreatives_me_creatives_rows
  >;
}) {
  const { user } = useUser();
  const [availableMetricsById] = useAvailableMetricsByIdAtom();
  const [showPlatformMetrics, setShowPlatformMetrics] = useState(false);
  const { selectedAdsByAdKey } = useReportBodyState();

  const dateLabels = useDateRangeAsDates();

  const metricsBySalesTableMetridId = useMemo(() => {
    const allMetrics = Object.values(availableMetricsById);
    const allSalesTableIdMetrics = allMetrics.filter(
      (spec) => spec.metadata.salesTableMetricId,
    );
    return keyBy(
      allSalesTableIdMetrics,
      (spec) => spec.metadata.salesTableMetricId,
    ) as Record<string, SalesPageMetric>;
  }, [availableMetricsById]);

  const dateLabelsHash = dateLabels.reduce<{ [dateLabel: string]: number }>(
    (acc, curr, index) => {
      acc[curr] = index;
      return acc;
    },
    {},
  );

  const metricSpec = availableMetricsById[metric];
  const { platformMetricsByAdId, hasPlatformMetrics } = useMemo(() => {
    const platformMetricsByAdId: { [adKey: string]: SalesPageMetric | null } =
      {};
    let hasPlatformMetrics = false;
    Object.entries(selectedAdsByAdKey).forEach(([adKey]) => {
      const ad = adsWithFullDailyMetricsByAdKey[adKey];
      if (!ad) {
        return;
      }
      const platformMetricSpec = getPlatformMetricFromAd(
        ad,
        metricSpec,
        availableMetricsById,
      );
      if (platformMetricSpec) {
        hasPlatformMetrics = true;
      }
      platformMetricsByAdId[adKey] = platformMetricSpec;
    });
    return { platformMetricsByAdId, hasPlatformMetrics };
  }, [
    adsWithFullDailyMetricsByAdKey,
    availableMetricsById,
    metricSpec,
    selectedAdsByAdKey,
  ]);

  const chartData = useMemo(() => {
    const data: GraphData[] = [];
    Object.entries(selectedAdsByAdKey).forEach(([adKey, adSelection]) => {
      const ad = adsWithFullDailyMetricsByAdKey[adKey];
      if (!ad) {
        return;
      }
      const adData: (number | null)[] = new Array(dateLabels.length).fill(null);
      const dailyMetrics =
        adsWithFullDailyMetricsByAdKey?.[ad.adKey]?.dailyMetrics;
      const platformSpec = platformMetricsByAdId[adKey];

      const partialOfChartPayload = {
        label: adSelection.adVisualId,
        colouring: adSelection.adColouring,
        tooltipData: {
          imgUrl: ad.imageUri ?? "",
          name: ad.name ?? "",
        },
      };

      if (!dailyMetrics?.length) {
        if (platformSpec && showPlatformMetrics) {
          data.push({
            ...partialOfChartPayload,
            data: new Array<number>(dateLabels.length).fill(0),
            tooltipData: {
              ...partialOfChartPayload.tooltipData,
              platform: getPlatformName(ad),
            },
          });
        }
        data.push({
          ...partialOfChartPayload,
          data: new Array<number>(dateLabels.length).fill(0),
        });
      }

      dailyMetrics?.forEach((metricDay) => {
        const indxOnAdData = dateLabelsHash[metricDay.date];
        const modifiedDay = (
          metricSpec?.requiredNBCalculatedMetrics || []
        ).reduce((prev, requiredMetric) => {
          const reqMetricSpec =
            availableMetricsById[requiredMetric] ??
            metricsBySalesTableMetridId[requiredMetric];

          return {
            ...prev,
            [requiredMetric]: reqMetricSpec.calculate(metricDay),
          };
        }, metricDay);

        adData[indxOnAdData] = calculateValue(
          metricSpec.calculate,
          modifiedDay,
        );
      });

      if (platformSpec && showPlatformMetrics) {
        const adPlatformData: (number | null)[] = new Array(
          dateLabels.length,
        ).fill(null);
        dailyMetrics?.forEach((metricDay) => {
          const indxOnAdData = dateLabelsHash[metricDay.date];

          const modifiedDay = (
            metricSpec?.requiredNBCalculatedMetrics || []
          ).reduce((prev, requiredMetric) => {
            const reqMetricSpec =
              availableMetricsById[requiredMetric] ??
              metricsBySalesTableMetridId[requiredMetric];

            return {
              ...prev,
              [requiredMetric]: reqMetricSpec.calculate(metricDay),
            };
          }, metricDay);

          adPlatformData[indxOnAdData] = calculateValue(
            platformSpec.calculate,
            modifiedDay,
          );
        });
        data.push({
          ...partialOfChartPayload,
          data: adPlatformData,
          tooltipData: {
            ...partialOfChartPayload.tooltipData,
            platform: getPlatformName(ad),
          },
        });
      }

      data.push({
        ...partialOfChartPayload,
        data: adData,
      });
    });
    return data;
  }, [
    adsWithFullDailyMetricsByAdKey,
    availableMetricsById,
    dateLabels.length,
    metricSpec.calculate,
    metricSpec.requiredNBCalculatedMetrics,
    dateLabelsHash,
    metricsBySalesTableMetridId,
    platformMetricsByAdId,
    selectedAdsByAdKey,
    showPlatformMetrics,
  ]);

  return (
    <ScrollElement
      className={clsx(
        "w-[-webkit-fill-available] px-15 py-2 min-h-[25rem] max-h-[30rem] bg-v2-primary",
        className,
      )}
      name={metric}
      id={metric}
    >
      <div className="flex flex-row mx-10">
        <div className="font-semibold">{availableMetricsById[metric].name}</div>
        {hasPlatformMetrics && (
          <div className="ml-auto flex flex-row items-center">
            <div>Show in Platform Metrics</div>
            <div className="ml-5 flex items-center">
              <LightSwitch
                id={`${metric}-graph-switch`}
                size="small"
                isSet={showPlatformMetrics}
                disabled={false}
                onChange={async () => {
                  setShowPlatformMetrics(!showPlatformMetrics);
                }}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mx-10 h-full relative">
        <Graph
          dateLabels={dateLabels}
          chartData={chartData}
          tooltipLabelFormatter={(tooltipItem) => {
            const label = tooltipItem.dataset.label;
            return `${label}: ${metricSpec.formatter(
              Number(tooltipItem.raw),
              user.currencies.currentCurrency,
              { precision: "exact" },
            )}`;
          }}
          yLabelFormatter={(value) =>
            metricSpec.formatter(value, user.currencies.currentCurrency, {
              precision: "approximate",
            })
          }
        />
      </div>
    </ScrollElement>
  );
}

type GraphData = {
  label: string;
  data: (number | null)[];
  tooltipData: {
    imgUrl: string;
    platform?: string;
    name: string;
  };
  colouring: {
    borderColor: string;
    backgroundColor: string;
    pointBackgroundColor: string;
    pointBorderColor: string;
  };
};

function Graph({
  dateLabels,
  chartData,
  tooltipLabelFormatter,
  yLabelFormatter,
}: {
  tooltipLabelFormatter: (value: TooltipItem<"bar" | "line">) => string;
  yLabelFormatter: (value: number) => string;
  dateLabels: ChartData<"line", ScatterDataPoint[], string>["labels"];
  chartData: GraphData[];
}) {
  const [graphType] = useGraphTypeAtom();
  const { designElementManager } = useDesignElementManager();

  const tooltipHandler = useCallback(
    (context: { chart: any; tooltip: any }) => {
      externalTooltipHandler(context, designElementManager);
    },
    [designElementManager],
  );

  const data = useMemo(() => {
    return chartData.map(({ label, data, colouring, tooltipData }) => ({
      tooltipData,
      label,
      data,
      yAxisID: "metricY",
      tensions: 0.3,
      pointRadius: 0,
      ...colouring,
    }));
  }, [chartData]);
  const lineDataset: ChartDataset<"line", (number | null)[]>[] = [...data];

  const barDataset: ChartDataset<"bar", (number | null)[]>[] = [...data].map(
    (d) => ({ ...d, maxBarThickness: 30 }),
  );

  if (graphType === "bar") {
    return (
      <Bar
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            title: {
              display: true,
              align: "start",
              font: { size: 18 },
            },
            legend: { display: false },
            tooltip: {
              enabled: false,
              callbacks: {
                label: (tooltipItem) => tooltipLabelFormatter(tooltipItem),
              },
              external: tooltipHandler,
            },
          },
          scales: {
            xAxis: {
              type: "time",
              time: {
                tooltipFormat: "ddd D MMM",
                unit: "day",
              },
              ticks: {
                font: { size: 13 },
                maxRotation: 20,
              },
              grid: {
                drawOnChartArea: false,
                drawTicks: true,
              },
            },
            metricY: {
              ticks: {
                callback: (tickValue) => yLabelFormatter(Number(tickValue)),
              },
              beginAtZero: true,
              type: "linear",
              display: true,
              position: "left",
              grid: {
                drawOnChartArea: true,
              },
            },
          },
          layout: { padding: { bottom: 36 } },
        }}
        data={{ labels: dateLabels, datasets: barDataset }}
      />
    );
  }

  return (
    <Line
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          title: {
            display: true,
            align: "start",
            font: { size: 18 },
          },
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: tooltipHandler,
            callbacks: {
              label: (tooltipItem) => tooltipLabelFormatter(tooltipItem),
            },
          },
        },
        scales: {
          xAxis: {
            type: "time",
            time: {
              tooltipFormat: "ddd D MMM",
              unit: "day",
            },
            ticks: {
              font: { size: 13 },
              maxRotation: 20,
            },
            grid: {
              drawOnChartArea: false,
              drawTicks: true,
            },
          },
          metricY: {
            ticks: {
              callback: (tickValue) => yLabelFormatter(Number(tickValue)),
            },
            beginAtZero: true,
            type: "linear",
            display: true,
            position: "left",
            grid: { drawOnChartArea: true },
          },
        },
        layout: {
          padding: { bottom: 36 },
        },
      }}
      data={{
        labels: dateLabels,
        datasets: lineDataset,
      }}
    />
  );
}

const getOrCreateTooltip = (chart: any, tooltip: any) => {
  let tooltipEl = chart.canvas.parentNode.querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.className =
      "bg-v2-primary border-gray-0 border-[1px] rounded-md text-interstellar min-w-[15rem] z-[1]";

    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.transition = "all .1s ease";

    const table = document.createElement("table");
    table.style.margin = "0px";

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }
  const padding = 40;

  if (chart.width - tooltip.x < tooltip.width + padding) {
    tooltipEl.style.transform = "translate(-100%, 0)";
  } else if (tooltip.x < tooltip.width) {
    // do nothing
    tooltipEl.style.transform = "translate(-10px, 0)";
  } else {
    tooltipEl.style.transform = "translate(-50%, 0)";
  }

  return tooltipEl;
};

function externalTooltipHandler(
  context: { chart: any; tooltip: any },
  designElementManager: DesignElementManagerType,
) {
  // Tooltip Element
  const { chart, tooltip } = context;

  const tooltipEl = getOrCreateTooltip(chart, tooltip);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b: any) => b.lines);

    const tableHead = document.createElement("thead");

    titleLines.forEach((title: string) => {
      const tr = document.createElement("tr");
      tr.style.borderWidth = "0px";

      const th = document.createElement("th");
      th.style.borderWidth = "0px";
      const text = document.createTextNode(title);

      th.appendChild(text);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });
    const hasPlatformMetrics = tooltip.dataPoints.some(
      (item: { dataset: { tooltipData: { platform: any } } }) =>
        item.dataset.tooltipData.platform,
    );

    const tableBody = document.createElement("tbody");
    bodyLines.forEach((body: string, i: string | number) => {
      const colors = tooltip.labelColors[i];

      const span = document.createElement("span");
      span.className = "rounded-sm";
      span.style.background = colors.backgroundColor;
      span.style.borderColor = colors.borderColor;
      span.style.borderWidth = "2px";
      span.style.marginRight = "10px";
      span.style.height = "10px";
      span.style.width = "10px";
      span.style.display = "inline-block";

      const tr = document.createElement("tr");
      tr.style.backgroundColor = "inherit";
      tr.style.borderWidth = "0";

      const td = document.createElement("td");
      td.className = "flex flex-row items-center mt-2";
      td.style.borderWidth = "0";

      const text = document.createTextNode(body);
      const imgDiv = document.createElement("div");
      imgDiv.className =
        "h-[3rem] w-[2rem] mr-2 rounded-md flex justify-center";
      if (tooltip.dataPoints[i].dataset.tooltipData.imgUrl) {
        const img = document.createElement("img");
        img.className = "h-[3rem] rounded-md object-scale-down";
        img.src = tooltip.dataPoints[i].dataset.tooltipData.imgUrl;
        img.alt = body;
        imgDiv.appendChild(img);
      }

      td.appendChild(imgDiv);
      td.appendChild(span);

      const platform = tooltip.dataPoints[i].dataset.tooltipData.platform as
        | string
        | undefined;

      if (hasPlatformMetrics) {
        const iconDiv = document.createElement("div");
        const icon = designElementManager
          .getDesignElement(platform ?? "northbeam")
          .getComponent({ size: "20px", className: "mr-2" });
        const iconHTMLString = renderToString(icon);
        iconDiv.innerHTML = iconHTMLString;
        td.appendChild(iconDiv);
      }

      const textDiv = document.createElement("div");
      const subtitleTextDiv = document.createElement("div");
      subtitleTextDiv.className = "text-xxxs text-subtitle truncate";
      const subtitleText = document.createTextNode(
        tooltip.dataPoints[i].dataset.tooltipData.name,
      );
      subtitleTextDiv.appendChild(subtitleText);
      textDiv.appendChild(text);
      textDiv.appendChild(subtitleTextDiv);

      td.appendChild(textDiv);
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector("table");

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = positionX + tooltip.caretX + "px";
  tooltipEl.style.top = positionY + tooltip.caretY + "px";
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding =
    tooltip.options.padding + "px " + tooltip.options.padding + "px";
}
